import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_URL, useSeo } from "@/hooks/useSeo";
import { contactChannels, siteInfo } from "@/mocks/site";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daim4c3p14a1h3mr76p0";
const MESSAGE_LIMIT = 500;

const topics = ["二创投稿", "内容合作", "授权咨询", "内容纠错", "其它问题"];

interface FormApiResult {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  useSeo({
    title: "联系我们 - 投稿、合作与内容纠错 | 131吃瓜",
    description:
      "想给131吃瓜投稿二创、洽谈内容合作、咨询素材授权或指出内容错误？在这里留下你的联系方式与说明，我们会尽快回复。",
    keywords: "联系131吃瓜,梗图投稿,表情包合作,内容纠错,授权咨询",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": `${SITE_URL}/contact#contactpage`,
          url: `${SITE_URL}/contact`,
          name: "联系我们",
          description: "131吃瓜的投稿、合作与纠错联系方式。",
          inLanguage: "zh-CN",
          isPartOf: { "@id": `${SITE_URL}/#website` },
        },
        {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          name: siteInfo.name,
          url: `${SITE_URL}/`,
          email: siteInfo.email,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "内容合作",
            email: siteInfo.email,
            availableLanguage: ["zh-CN"],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "联系我们", item: `${SITE_URL}/contact` },
          ],
        },
      ],
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormApiResult | null = null;
      try {
        parsed = JSON.parse(responseText) as FormApiResult;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交没有成功，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus("error");
      setFormError("网络好像不太稳定，请稍后再试一次。");
    }
  };

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: "联系我们" }]} />
          <h1 className="mt-6 font-heading text-3xl text-foreground-950 md:text-5xl">联系我们</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-lg">
            投稿、合作、授权咨询或者只是想说一句「这个梗分类错了」，都可以从这里找到我们。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-[1fr_360px] lg:gap-14">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            <h2 className="font-heading text-2xl text-foreground-950">给我们留言</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              带 * 的为必填项。说明越具体，我们回复得越快。
            </p>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-8 space-y-5"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm text-foreground-800">
                    怎么称呼你 *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="昵称或姓名都可以"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm text-foreground-800">
                    联系邮箱 *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="方便我们回信"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="topic" className="mb-2 block text-sm text-foreground-800">
                  想聊的事
                </label>
                <select
                  id="topic"
                  name="topic"
                  defaultValue={topics[0]}
                  className="w-full appearance-none rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                >
                  {topics.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="message" className="block text-sm text-foreground-800">
                    具体说明 *
                  </label>
                  <span className={`text-xs ${messageLength > MESSAGE_LIMIT - 40 ? "text-primary-600" : "text-foreground-500"}`}>
                    {messageLength}/{MESSAGE_LIMIT}
                  </span>
                </div>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={MESSAGE_LIMIT}
                  onChange={(event) => setMessageLength(event.target.value.length)}
                  placeholder="可以附上作品链接、想投稿的梗，或需要纠正的地方"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                ></textarea>
              </div>

              <div className="form-guard-field" aria-hidden="true">
                <label htmlFor="website_alt">请勿填写此字段</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-background-300"
                >
                  {status === "submitting" ? "正在提交…" : "提交留言"}
                </button>
                <p className="text-xs leading-relaxed text-foreground-500">
                  提交后我们会在两个工作日内通过邮件回复你。
                </p>
              </div>

              {status === "success" && (
                <p className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 p-4 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base"></i>
                  留言已经收到，感谢你花时间写这些，我们会尽快回复。
                </p>
              )}
              {status === "error" && (
                <p className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 p-4 text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5 text-base"></i>
                  {formError || "提交没有成功，请稍后重试。"}
                </p>
              )}
            </form>
          </div>

          <aside className="space-y-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-lg text-foreground-950">其它联系方式</h2>
              <ul className="mt-4 space-y-5">
                {contactChannels.map((channel) => (
                  <li key={channel.label} className="flex gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${channel.icon} text-lg`}></i>
                    </span>
                    <div>
                      <h3 className="text-sm text-foreground-900">{channel.label}</h3>
                      <p className="mt-0.5 text-sm text-primary-700">{channel.value}</p>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{channel.note}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h2 className="font-heading text-lg text-foreground-950">投稿小提示</h2>
              <ul className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-700">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600"></i>
                  附上原图或高清链接，方便我们核对细节。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600"></i>
                  如果是二创作品，请注明希望显示的署名。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600"></i>
                  说明适合放进哪个分类，收录会更快。
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}